import { useMemo, useRef, useState } from "react";
import { useStyles } from "./RCAFisboneDiagram.styles";
import AddIcon from "@mui/icons-material/Add";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import EditFishboneProblemStatementModal from "./components/EditFishboneProblemStatementModal.view";
import EditItemModal from "./components/EditItemModal.view";
import AddItemModal from "./components/AddNewMainItemModal.view";
import AddNewSecondaryItemModal from "./components/AddNewSecondaryItemModal";
import DeleteItemModal from "./components/DeleteItemModal.view";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import {
    IFishboneDiagramCategory,
    IFishboneDiagramProblemCause,
} from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { IRCAFishboneDiagram } from "./RCAFishboneDiagramInterfaces";
import useFishboneDiagram from "./useFishboneDiagram";
import RCAFishboneDiagramCategory from "./components/RCAFishboneDiagramCategory";
import { useTranslation } from "react-i18next";
import { truncateLongText } from "utilities/utilities";
import { Tooltip, Typography } from "@mui/material";
import { TRUNCATE_PROBLEM_STATEMENT_LENGTH_FISHBONE_DIAGRAM } from "constants/constants";
import useRolesPermissions from "hooks/useRolesPermissions";

const RCAFisboneDiagram = ({ fishboneDiagram, rca, onSaveFishboneDiagram }: IRCAFishboneDiagram) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const rcaId = useMemo(() => rca.rca_id, [rca]);

    const rcaOwnershipIds = useMemo(
        () =>
            rca
                ? [
                      ...(rca?.owner?.user_id ? [rca.owner.user_id] : []),
                      ...(rca?.created_by ? [rca?.created_by] : []),
                      ...(rca?.rca_team_members ? rca?.rca_team_members?.map((rtm) => rtm.user.user_id) : []),
                  ]
                : [],
        [rca],
    );
    const { RCA_PERMISSIONS } = useRolesPermissions(rcaOwnershipIds);

    const { fishboneCategories, loading: isLoadingFishboneCategories } = RCALibraryEntity.GetFishboneCategories();

    const mainHorizontalLineWrapperRef = useRef<HTMLDivElement | null>(null);
    const mainDiagramWrapperRef = useRef<HTMLDivElement | null>(null);

    const verticalUpArrowsRefs = useRef<HTMLDivElement[]>([]);
    const verticalDownArrowsRefs = useRef<HTMLDivElement[]>([]);

    const [problemStatementEditText, setProblemStatementEditText] = useState<string | null>(null);
    const [itemToEdit, setItemToEdit] = useState<IFishboneDiagramProblemCause | null>(null);
    const [newMainItemCategory, setNewMainItemCategory] = useState<IFishboneDiagramCategory | null>(null);
    const [newSecondaryItemParentData, setNewSecondaryItemParentData] = useState<IFishboneDiagramProblemCause | null>(
        null,
    );

    const [itemToDelete, setItemToDelete] = useState<IFishboneDiagramProblemCause | null>(null);

    const {
        createOrEditFishboneDiagram,
        createOrEditFishboneDiagramProblemCause,
        topCategories,
        minimalVerticalLineHeightTop,
        bottomCategories,
        calculateFisboneHeight,
        fishboneDiagramProblemCausesData,
        fishboneDiagramData,
        handleOnRemoveItem,
    } = useFishboneDiagram({
        onCreateOrEditFishboneDiagram: onSaveFishboneDiagram,
        fishboneDiagram,
        fishboneCategories,
        isLoadingFishboneCategories,
        mainHorizontalLineWrapperRef,
        verticalUpArrowsRefs,
        verticalDownArrowsRefs,
        mainDiagramWrapperRef,
    });

    return (
        <section className={classes.fishboneDiagram} ref={mainDiagramWrapperRef}>
            <div className={classes.mainHorizontalLine} style={{ top: minimalVerticalLineHeightTop }}>
                <div className={classes.mainHorizontalLineWrapper} ref={mainHorizontalLineWrapperRef}>
                    {topCategories.map((category, index) => (
                        <RCAFishboneDiagramCategory
                            name={category.name}
                            key={index}
                            xPosition={category.position}
                            position="top"
                            ref={(element) => {
                                if (element) verticalUpArrowsRefs.current[index] = element;
                            }}
                            onComponentReady={() => calculateFisboneHeight()}
                            items={fishboneDiagramProblemCausesData.filter(
                                (problemCause) =>
                                    problemCause.fishbone_diagram_category.fishbone_diagram_category_id ===
                                    category.fishbone_diagram_category_id,
                            )}
                            category={category}
                            onRemoveItem={(item) => setItemToDelete(item)}
                            onEditItem={(item) => setItemToEdit(item)}
                            onHighlightItem={async (item) =>
                                await createOrEditFishboneDiagramProblemCause({
                                    rcaId,
                                    fishboneDiagramCategoryId:
                                        item.fishbone_diagram_category.fishbone_diagram_category_id,
                                    fishboneDiagramId: fishboneDiagram?.fishbone_diagram_id,
                                    primaryProblemCauseId: item.primary_problem_cause_id,
                                    descriptionText: item.description,
                                    fishboneDiagramProblemCauseId: item.fishbone_diagram_problem_cause_id,
                                    isHighlighted: !item.is_highlighted,
                                })
                            }
                            onAddMainItem={(category) => setNewMainItemCategory(category)}
                            onAddSecondaryItem={(parentMainItem) => setNewSecondaryItemParentData(parentMainItem)}
                        />
                    ))}
                    {bottomCategories.map((category, index) => (
                        <RCAFishboneDiagramCategory
                            name={category.name}
                            key={index}
                            position="bottom"
                            xPosition={category.position}
                            ref={(element) => {
                                if (element) verticalDownArrowsRefs.current[index] = element!;
                            }}
                            onComponentReady={() => calculateFisboneHeight()}
                            items={fishboneDiagramProblemCausesData.filter(
                                (problemCause) =>
                                    problemCause.fishbone_diagram_category.fishbone_diagram_category_id ===
                                    category.fishbone_diagram_category_id,
                            )}
                            category={category}
                            onRemoveItem={(item) => setItemToDelete(item)}
                            onEditItem={(item) => setItemToEdit(item)}
                            onHighlightItem={async (item) =>
                                await createOrEditFishboneDiagramProblemCause({
                                    rcaId,
                                    fishboneDiagramCategoryId:
                                        item.fishbone_diagram_category.fishbone_diagram_category_id,
                                    fishboneDiagramId: fishboneDiagram?.fishbone_diagram_id,
                                    primaryProblemCauseId: item.primary_problem_cause_id,
                                    descriptionText: item.description,
                                    fishboneDiagramProblemCauseId: item.fishbone_diagram_problem_cause_id,
                                    isHighlighted: !item.is_highlighted,
                                })
                            }
                            onAddMainItem={(category) => setNewMainItemCategory(category)}
                            onAddSecondaryItem={(parentMainItem) => setNewSecondaryItemParentData(parentMainItem)}
                        />
                    ))}
                    <div className={classes.arrowRight}></div>
                    <div className={classes.head}>
                        <div className={classes.headContent}>
                            <svg
                                width="164"
                                height="238"
                                viewBox="0 0 164 238"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0.900334 237.713C11.8372 185.55 18.0754 148.678 18.0754 109.377C18.0754 70.0766 11.8372 33.2037 0.900204 1.04085C5.26289 0.878046 9.68073 0.794922 14.1543 0.794922C96.7293 0.794922 163.76 49.7616 163.76 109.377C163.76 168.992 96.7293 237.959 14.1543 237.959C9.68078 237.959 5.26297 237.876 0.900334 237.713Z"
                                    fill="#F6E4E4"
                                />
                            </svg>
                            <div className={classes.headTitle}>
                                {t("rca.rca_library.rca_detail.fishbone_diagram.problem_statement")}
                            </div>
                            {fishboneDiagramData.problem_statement && (
                                <>
                                    {fishboneDiagramData.problem_statement.length >=
                                    TRUNCATE_PROBLEM_STATEMENT_LENGTH_FISHBONE_DIAGRAM ? (
                                        <Tooltip
                                            PopperProps={{
                                                className: classes.tooltip,
                                            }}
                                            title={
                                                <Typography className={classes.tooltipTexts}>
                                                    {fishboneDiagramData.problem_statement}
                                                </Typography>
                                            }
                                            placement="top-start"
                                        >
                                            <div className={classes.headDescription}>
                                                {truncateLongText(
                                                    fishboneDiagramData.problem_statement,
                                                    TRUNCATE_PROBLEM_STATEMENT_LENGTH_FISHBONE_DIAGRAM,
                                                )}
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <div className={classes.headDescription}>
                                            {fishboneDiagramData.problem_statement}
                                        </div>
                                    )}
                                    <div
                                        id="headEditButton"
                                        className={classes.headEditButton}
                                        onClick={() => {
                                            setProblemStatementEditText(fishboneDiagramData.problem_statement || "");
                                        }}
                                    >
                                        <ModeEditIcon />
                                    </div>
                                </>
                            )}
                            {!fishboneDiagramData.problem_statement && (
                                <div
                                    id="headAddProblemButton"
                                    className={classes.headAddButton}
                                    onClick={() => {
                                        setProblemStatementEditText(fishboneDiagramData.problem_statement || "");
                                    }}
                                >
                                    <AddIcon />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={classes.tail}>
                        <svg
                            width="64"
                            height="118"
                            viewBox="0 0 64 118"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10.3636 59.2485C9.78749 60.1274 9.23286 61.0277 8.70082 61.9493C-1.50173 79.6206 -0.808722 100.593 8.69549 117.086C27.7316 117.07 46.2404 107.185 56.443 89.5132C63.0636 78.0458 65.0962 65.1886 63.1101 53.0671C62.7326 44.5083 60.3483 35.9111 55.7686 27.9789C45.5661 10.3076 27.0573 0.423723 8.02115 0.405762C-1.48312 16.8995 -2.17615 37.8716 8.02641 55.5429C8.76314 56.819 9.54318 58.0544 10.3636 59.2485Z"
                                fill="#F6E4E4"
                            />
                        </svg>
                    </div>
                </div>
            </div>
            <EditFishboneProblemStatementModal
                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                problemStatementText={problemStatementEditText}
                open={problemStatementEditText !== null}
                onClose={() => setProblemStatementEditText(null)}
                onSaveProblemStatement={(newProblemStatementText) => {
                    createOrEditFishboneDiagram({
                        rcaId,
                        problemStatement: newProblemStatementText,
                        fishboneDiagramId: fishboneDiagram?.fishbone_diagram_id,
                    });
                }}
            />
            <EditItemModal
                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                itemToEdit={itemToEdit}
                open={!!itemToEdit}
                onClose={() => setItemToEdit(null)}
                onEditItem={async (item) => {
                    await createOrEditFishboneDiagramProblemCause({
                        rcaId,
                        fishboneDiagramCategoryId: item.fishbone_diagram_category.fishbone_diagram_category_id,
                        fishboneDiagramId: fishboneDiagram?.fishbone_diagram_id,
                        primaryProblemCauseId: item.primary_problem_cause_id,
                        descriptionText: item.description,
                        fishboneDiagramProblemCauseId: item.fishbone_diagram_problem_cause_id,
                        isHighlighted: item.is_highlighted,
                    });
                }}
            />
            <AddItemModal
                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                onClose={() => setNewMainItemCategory(null)}
                open={!!newMainItemCategory}
                category={newMainItemCategory}
                onSaveNewMainItem={async (category, descriptionText) => {
                    await createOrEditFishboneDiagramProblemCause({
                        rcaId,
                        fishboneDiagramCategoryId: category.fishbone_diagram_category_id,
                        fishboneDiagramId: fishboneDiagram?.fishbone_diagram_id,
                        primaryProblemCauseId: null,
                        descriptionText,
                        isHighlighted: false,
                    });
                }}
            />
            <AddNewSecondaryItemModal
                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                open={!!newSecondaryItemParentData}
                onClose={() => setNewSecondaryItemParentData(null)}
                parentMainItem={newSecondaryItemParentData}
                onSaveNewSecondaryItem={async (parentMainItem, descriptionText) => {
                    await createOrEditFishboneDiagramProblemCause({
                        rcaId,
                        fishboneDiagramCategoryId:
                            parentMainItem.fishbone_diagram_category.fishbone_diagram_category_id,
                        fishboneDiagramId: fishboneDiagram?.fishbone_diagram_id,
                        primaryProblemCauseId: parentMainItem.fishbone_diagram_problem_cause_id,
                        descriptionText: descriptionText,
                        isHighlighted: false,
                    });
                }}
            />
            <DeleteItemModal
                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                itemToDelete={itemToDelete}
                open={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onDeleteItem={(item) => {
                    handleOnRemoveItem(item);
                    setItemToDelete(null);
                }}
            />
        </section>
    );
};

export default RCAFisboneDiagram;
